import React, { useCallback, useEffect, useMemo } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import useStores from 'shared/hooks/use-stores';
import { OrderType } from 'types/graphql';
import { useCheckout } from 'src/checkout/context';
import { DUTCHIE_PAY, FASTER } from 'src/payments/constants';
import { useIncentives } from 'src/payments/hooks/use-incentives/use-incentives';

import { useCartPayment } from 'src/checkout/components/sections/payment/shared/use-cart-payment';
import { TabValue, usePaymentSelectionContext } from 'src/checkout/components/sections/payment/shared/context';
import { TabOption } from './payment-tabs';
import { BoltIcon } from './icons/bolt-icon';

export type UseSelectionsResponse = {
  showPaymentBanner: boolean;
  tabOptions: TabOption[];
  onTabChange: (newPaymentMethod: string) => void;
  tabValue: TabValue;
  showFeeBadgeInline: boolean;
  showTabs: boolean;
  showOnlinePaymentOptionOnly: boolean;
  showInstorePaymentOptionOnly: boolean;
  showPaymentSummary: boolean;
  plaidEmbeddedSearchVisible: boolean;
  unenrolledAlertMessage: string;
};

export const useSelections = (): UseSelectionsResponse => {
  const flags = useFlags();
  const {
    expanded,
    setExpanded,
    tabValue,
    setTabValue,
    creditCardError,
    setCreditCardError,
  } = usePaymentSelectionContext();

  const disableTabFees: boolean = flags['fintech.platform.remove-tab-fees.rollout'];
  // Hooks
  const {
    isEnrolledDutchiePay,
    isDutchiePayEnabledForDispo,
    paymentMethod,
    paymentMethodId,
    hasOtherOrderTypesWithPBBEnabled,
    onlinePaymentOption,
    onlinePaymentOptionValue,
    hasOnlineAndOfflinePaymentOptions,
    onlinePaymentOptionFeeLabel,
    maxInstoreFeeLabel,
    onlinePaymentOptionIsCreditCard,
    defaultOfflinePaymentValue,
    offlinePaymentOptionValues,
    loggedInPBBSignupEnabled,
    needsRelinkBankAccount: relinkRequired,
  } = useCartPayment();

  const { Cart, User } = useStores();
  const { orderType, dispensary } = Cart;

  const { id } = User ?? {};
  const { showPaymentBanner, qualifiedPBBUser, payerId } = useIncentives();
  const Checkout = useCheckout();

  const requirePaymentMethodSelection = dispensary?.storeSettings?.requirePaymentMethodSelection ?? false;

  const checkoutPaymentError = Checkout.getError({ card: 'payment' });

  const isDeliveryOrder = orderType === OrderType.delivery;

  const onlinePaymentError =
    tabValue === TabValue.online && (!!checkoutPaymentError?.error || relinkRequired || creditCardError !== '');

  const unenrolledAlertMessage = checkoutPaymentError.error ? checkoutPaymentError.message : '';

  const tabOptions = useMemo(
    () =>
      hasOnlineAndOfflinePaymentOptions
        ? [
            {
              value: TabValue.online,
              label: 'Pay now',
              sublabel: disableTabFees ? '' : '(faster)',
              badgeLabel: onlinePaymentOptionFeeLabel,
              badgeIcon: onlinePaymentOptionFeeLabel === FASTER ? <BoltIcon /> : undefined,
              hasError: onlinePaymentError,
            },
            {
              value: TabValue.offline,
              label: `Pay ${isDeliveryOrder ? 'on delivery' : 'in-store'}`,
              badgeLabel: maxInstoreFeeLabel,
            },
          ]
        : [],
    [
      hasOnlineAndOfflinePaymentOptions,
      onlinePaymentOptionFeeLabel,
      maxInstoreFeeLabel,
      isDeliveryOrder,
      disableTabFees,
      onlinePaymentError,
    ]
  );

  // Show
  const isLoggedIn = !!id;

  const showTabs = expanded && hasOnlineAndOfflinePaymentOptions && tabOptions.length > 0;

  const showOnlinePaymentOptionOnly =
    expanded && !showTabs && (!!onlinePaymentOption || hasOtherOrderTypesWithPBBEnabled);

  const showInstorePaymentOptionOnly = expanded && !showTabs && offlinePaymentOptionValues.includes(paymentMethod);
  const showPaymentSummary = !expanded && !!onlinePaymentOption;
  const plaidEmbeddedSearchVisible = tabValue === TabValue.online && loggedInPBBSignupEnabled;

  const setPaymentMethod = useCallback(
    (method: string, methodId: number | string | null, expand: boolean): void => {
      Cart.setPaymentMethod(method);
      Cart.setPaymentMethodId(methodId);
      setExpanded(expand);
    },
    [Cart, setExpanded]
  );

  // Effect: Sets the payment method based on Dutchie Pay eligibility and enrollment status.
  // - If requirePaymentMethodSelection is enabled and both online/offline options exist, don't pre-select.
  // - If Dutchie Pay is enabled and either the online payment option is Dutchie Pay or other order types with PBB are enabled:
  //   - Check if the user is enrolled in Dutchie Pay.
  //   - Set the payment method to Dutchie Pay, using the payment method ID if enrolled, otherwise null.
  //   - Mark the user as a first-time PBB user if qualified.
  // - Otherwise, set the payment method to the online payment option if it's a credit card, or to the default offline payment value.
  useEffect(() => {
    // When requirePaymentMethodSelection is enabled and there are both payment options, don't auto-select
    if (requirePaymentMethodSelection && hasOnlineAndOfflinePaymentOptions) {
      setPaymentMethod('', null, true);
      setTabValue(TabValue.none);
      Checkout.removeError({ card: 'payment' });
      return;
    }

    if ((isDutchiePayEnabledForDispo && onlinePaymentOptionValue === DUTCHIE_PAY) || hasOtherOrderTypesWithPBBEnabled) {
      const isEnrolled = isEnrolledDutchiePay && onlinePaymentOptionValue === DUTCHIE_PAY;
      setPaymentMethod(DUTCHIE_PAY, isEnrolled ? paymentMethodId : null, !isEnrolled || relinkRequired);
      Cart.setIsFirstTimePBBUser(qualifiedPBBUser);
      Cart.setPayerId(payerId);
      setTabValue(TabValue.online);
    } else if (onlinePaymentOptionIsCreditCard) {
      setPaymentMethod(onlinePaymentOptionValue, null, true);
      setTabValue(TabValue.online);
    } else {
      // set the payment method to the default offline payment value
      setPaymentMethod(defaultOfflinePaymentValue, null, true);
      setTabValue(TabValue.offline);
    }
    Checkout.removeError({ card: 'payment' });
  }, [
    isDutchiePayEnabledForDispo,
    onlinePaymentOptionIsCreditCard,
    onlinePaymentOptionValue,
    isEnrolledDutchiePay,
    paymentMethodId,
    setPaymentMethod,
    qualifiedPBBUser,
    Cart,
    hasOtherOrderTypesWithPBBEnabled,
    hasOnlineAndOfflinePaymentOptions,
    requirePaymentMethodSelection,
    orderType,
    isLoggedIn,
    defaultOfflinePaymentValue,
    setTabValue,
    payerId,
    relinkRequired,
    Checkout,
  ]);

  // When the tab changes, set the payment method to the selected tabs payment method
  const onTabChange = (selectedTab: TabValue): void => {
    const newPaymentMethod = selectedTab === TabValue.online ? onlinePaymentOptionValue : defaultOfflinePaymentValue;
    Cart.setPaymentMethod(newPaymentMethod);
    Cart.removeTip();
    setTabValue(selectedTab);
    Checkout.removeError({ card: 'payment' });
    setCreditCardError('');
    if (newPaymentMethod === DUTCHIE_PAY) {
      Cart.setPaymentMethodId(paymentMethodId);
      Cart.setIsFirstTimePBBUser(qualifiedPBBUser);
      Cart.setPayerId(payerId);
    } else {
      Cart.setPaymentMethodId(null);
    }
  };

  return {
    showPaymentBanner,
    tabOptions,
    onTabChange,
    tabValue,
    showFeeBadgeInline: disableTabFees,
    showTabs,
    showOnlinePaymentOptionOnly,
    showInstorePaymentOptionOnly,
    showPaymentSummary,
    plaidEmbeddedSearchVisible,
    unenrolledAlertMessage,
  };
};
